import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ClubBook } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { reading } from "@pagemates/shared/copy";

const txt = reading.datesEditor;

const day = (iso: string | null | undefined) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");

/**
 * Admin form for a club book's dates. `finish` also moves the book to
 * Finished (used for back-catalogue books added straight from on deck).
 */
export function DatesEditor({ clubId, book, finish, onDone }: { clubId: string; book: ClubBook; finish?: boolean; onDone: () => void }) {
  const qc = useQueryClient();
  const [startedAt, setStartedAt] = useState(day(book.startedAt));
  const [finishedAt, setFinishedAt] = useState(day(book.finishedAt) || (finish ? new Date().toISOString().slice(0, 10) : ""));
  const showFinished = finish || book.status === "finished";
  const save = useMutation({
    mutationFn: () => api.updateBook(clubId, book.id, {
      ...(finish ? { status: "finished" as const } : {}),
      startedAt: startedAt || null,
      ...(showFinished ? { finishedAt: finishedAt || null } : {}),
    }),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["books", clubId] }); onDone(); },
  });
  return (
    <form className="card stack-sm dates" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <div className="row" style={{ flexWrap: "wrap", gap: 12 }}>
        <label className="field"><span>{txt.started}</span><input className="input" type="date" value={startedAt} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setStartedAt(e.target.value)} /></label>
        {showFinished && <label className="field"><span>{txt.finished}</span><input className="input" type="date" value={finishedAt} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setFinishedAt(e.target.value)} /></label>}
      </div>
      {finish && <p className="muted small">{txt.marksItFinishedWith}</p>}
      {save.isError && <p className="error">{describeError(save.error)}</p>}
      <div className="row">
        <button type="submit" className="btn primary sm" disabled={save.isPending}>{finish ? txt.addAsFinished : txt.saveDates}</button>
        <button type="button" className="btn ghost sm" onClick={onDone}>{txt.cancel}</button>
      </div>
    </form>
  );
}
